<script>
  import { format } from 'svelte-i18n';
  import { Alert, Card, FormGroup, Label, TabContent, TabPane } from '@sveltestrap/sveltestrap';

  import { CustomFields } from '@client/components/custom-fields';
  import { SwitchField } from '@client/components/fields/standalone';
  import { ParamFields } from '@client/components/param-fields';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { syncConnections } from '@client/stores/sync-connections';
  import { userStore } from '@client/stores/user';

  import { AUTO_PUSH_TYPES, DEFAULT_SIGNATURE_MODE_OPTIONS } from '@client/enums';

  import { DynamicMembers } from '../dynamic-members';
  import { GroupName } from '../group-name';
  import { GroupSelector } from '../group-selector';
  import { UsersSelector } from '../users-selector';

  export let group;
  export let invalid = false;
  export let includeUsers = [];
  export let excludeUsers = [];
  export let parameters = [];
  export let gmailpush = { automatic: false, pushtype: 'ptp' };
  export let outlookaddin = {};
  export let helpers = [];
  export let domainSettings = {};
  export let onChange = () => {};
  export let savedTick = 0;

  const PUSH_TYPE_OPTIONS = Object.entries(AUTO_PUSH_TYPES).map(([value, label]) => ({
    value,
    label: $format(label)
  }));

  const SIGNATURE_MODE_OPTIONS = DEFAULT_SIGNATURE_MODE_OPTIONS.map(({ value, label }) => ({
    value,
    label: $format(label)
  }));

  const onOutlookAddinChange = (property, value) => {
    outlookaddin[property] = value;
    outlookaddin = { ...outlookaddin };
  };
</script>

<div class="group-edit-form">
  <div class="section">
    <h2>{$format('label.NAME')}</h2>
    <GroupName bind:group bind:invalid />
  </div>
  <div class="section">
    <h2>{$format('label.MEMBERSHIP')}</h2>
    <Card class="pusher-config px-4 py-3 pt-2">
      <TabContent>
        <TabPane tabId="users" active>
          <span slot="tab">
            {$format('label.USERS')}
            <span class:dot={group.senders.length || group.senderexclusions.length} />
          </span>
          <div class="section">
            <UsersSelector bind:group bind:users={includeUsers} {onChange} />
          </div>
          <div class="section">
            <UsersSelector bind:group bind:users={excludeUsers} {onChange} exclusions />
          </div>
        </TabPane>
        {#if $syncConnections.checkgappsconnected}
          <TabPane tabId="google">
            <span slot="tab">
              {$format('label.GOOGLE')}<span
                class:dot={group.googlegroupbindings.length ||
                  group.googleorgbindings.length ||
                  group.exclusions.googlegroupbindings.length ||
                  group.exclusions.googleorgbindings.length}
              />
            </span>
            <div class="section">
              <GroupSelector bind:config={group} field="googlegroupbindings" {onChange} />
            </div>
            <div class="section">
              <GroupSelector bind:config={group} field="googlegroupbindings" exclusions {onChange} />
            </div>
            <div class="section">
              <GroupSelector bind:config={group} field="googleorgbindings" {onChange} />
            </div>
            <div class="section">
              <GroupSelector bind:config={group} field="googleorgbindings" exclusions {onChange} />
            </div>
          </TabPane>
        {/if}
        {#if $syncConnections.checko365connected}
          <TabPane tabId="m365">
            <span slot="tab">
              {$format('label.M365')}<span
                class:dot={group.o365groupbindings.length || group.exclusions.o365groupbindings.length}
              />
            </span>
            <div class="section">
              <GroupSelector bind:config={group} field="o365groupbindings" {onChange} />
            </div>
            <div class="section">
              <GroupSelector bind:config={group} field="o365groupbindings" exclusions {onChange} />
            </div>
          </TabPane>
        {/if}
        {#if $userStore.featuresConfig.csvtree.enabled}
          <TabPane tabId="workday">
            <span slot="tab">
              {$format('label.WORKDAY')}<span
                class:dot={group.csvtreenodebindings.length || group.exclusions.csvtreenodebindings.length}
              />
            </span>
            <div class="section">
              <GroupSelector bind:config={group} field="csvtreenodebindings" {onChange} />
            </div>
            <div class="section">
              <GroupSelector bind:config={group} field="csvtreenodebindings" exclusions {onChange} />
            </div>
          </TabPane>
        {/if}
        {#if $syncConnections.checkoktaconnected}
          <TabPane tabId="okta">
            <span slot="tab">
              {$format('label.OKTA')}<span
                class:dot={group.oktagroupbindings.length || group.exclusions.oktagroupbindings.length}
              />
            </span>
            <div class="section">
              <GroupSelector bind:config={group} field="oktagroupbindings" {onChange} />
            </div>
            <div class="section">
              <GroupSelector bind:config={group} field="oktagroupbindings" exclusions {onChange} />
            </div>
          </TabPane>
        {/if}
        <TabPane tabId="dynamic">
          <span slot="tab">
            {$format('label.DYNAMIC')}<span class:dot={group.userAttrs?.length} />
          </span>
          <div class="section">
            {#key group.groupname}
              <DynamicMembers bind:group {savedTick} />
            {/key}
          </div>
        </TabPane>
      </TabContent>
    </Card>
  </div>

  <div class="section">
    <h2>{$format('label.FIELDS')}</h2>
    <ParamFields bind:params={parameters} />
  </div>

  {#if domainSettings?.googleenabled}
    <div class="section">
      <h2>{$format('label.GOOGLE_PUSH')}</h2>
      <Card class="pusher-config p-4 pb-3">
        <FormGroup>
          <Label>{$format('label.AUTOMATIC_PUSH')}</Label>
          <div>
            <Switch bind:checked={gmailpush.automatic} />
          </div>
        </FormGroup>
        <FormGroup>
          <Label for="pushtype-select">{$format('label.PUSH_TYPE')}</Label>
          <Select
            id="pushtype-select"
            bind:value={gmailpush.pushtype}
            searchable={false}
            clearable={false}
            showChevron
            placeholder={$format('label.SELECT')}
            items={PUSH_TYPE_OPTIONS}
          />
        </FormGroup>
      </Card>
    </div>
  {/if}

  {#if domainSettings?.outlookaddin}
    {#if !domainSettings?.googleenabled}
      <div class="section mt-5 mb-4">
        <h2>{$format('label.ADDIN_CONTROLS')}</h2>
        <Card class="p-4 pt-1">
          <SwitchField
            id="outlookaddin-enableOverrides"
            label={$format('label.ENABLE_ADDIN_CONTROLS_OVERRIDE')}
            tooltip={$format('label.TOOLTIP.ENABLE_ADDIN_CONTROLS_OVERRIDE')}
            valueOn={$format('label.ENABLED')}
            valueOff={$format('label.DISABLED')}
            showActions={false}
            editing={true}
            checked={outlookaddin.enableOverrides}
            onChange={(id, label, value) => onOutlookAddinChange('enableOverrides', value)}
          />

          {#if outlookaddin.enableOverrides}
            <Alert color="warning" class="mt-3 mb-3">
              {@html $format('label.ADDIN_CONTROLS_OVERRIDE_WARNING')}
            </Alert>
          {/if}

          {#if outlookaddin.enableOverrides}
            <SwitchField
              id="outlookaddin-disableAutoInsertSignatures"
              label={$format('label.AUTOMATIC_SIGNATURES')}
              tooltip={$format('label.TOOLTIP.AUTOMATIC_SIGNATURES')}
              valueOn={$format('label.ENABLED')}
              valueOff={$format('label.DISABLED')}
              showActions={false}
              editing={true}
              inverted
              checked={outlookaddin.disableAutoInsertSignatures}
              onChange={(id, label, value) => onOutlookAddinChange('disableAutoInsertSignatures', value)}
            />

            <SwitchField
              id="outlookaddin-enableVisualPreview"
              label={$format('label.VISUAL_PREVIEW')}
              tooltip={$format('label.TOOLTIP.VISUAL_PREVIEW')}
              valueOn={$format('label.ENABLED')}
              valueOff={$format('label.DISABLED')}
              showActions={false}
              editing={true}
              checked={outlookaddin.enableVisualPreview}
              onChange={(id, label, value) => onOutlookAddinChange('enableVisualPreview', value)}
            />

            <SwitchField
              id="outlookaddin-disablePlainTextPreview"
              label={$format('label.PLAIN_TEXT_PREVIEW')}
              tooltip={$format('label.TOOLTIP.PLAIN_TEXT_PREVIEW')}
              valueOn={$format('label.ENABLED')}
              valueOff={$format('label.DISABLED')}
              showActions={false}
              editing={true}
              inverted
              checked={outlookaddin.disablePlainTextPreview}
              onChange={(id, label, value) => onOutlookAddinChange('disablePlainTextPreview', value)}
            />

            <div class="signature-mode-field">
              <Label for="outlookaddin-defaultSignatureMode">{$format('label.DEFAULT_SIGNATURE_MODE')}</Label>
              <Select
                id="outlookaddin-defaultSignatureMode"
                value={SIGNATURE_MODE_OPTIONS.find(({ value }) => value === outlookaddin.defaultSignatureMode)}
                on:change={(e) => onOutlookAddinChange('defaultSignatureMode', e.detail?.value)}
                searchable={false}
                clearable={false}
                showChevron
                placeholder={$format('label.SELECT')}
                items={SIGNATURE_MODE_OPTIONS}
              />
            </div>

            <SwitchField
              id="outlookaddin-disableRemoveDefaultSignature"
              label={$format('label.REMOVE_DEFAULT_SIGNATURE')}
              tooltip={$format('label.TOOLTIP.REMOVE_DEFAULT_SIGNATURE')}
              valueOn={$format('label.ENABLED')}
              valueOff={$format('label.DISABLED')}
              showActions={false}
              editing={true}
              inverted
              checked={outlookaddin.disableRemoveDefaultSignature}
              onChange={(id, label, value) => onOutlookAddinChange('disableRemoveDefaultSignature', value)}
            />

            <SwitchField
              id="outlookaddin-mergeParams"
              label={$format('label.MERGE_USER_FIELDS')}
              tooltip={$format('label.TOOLTIP.MERGE_USER_FIELDS')}
              valueOn={$format('label.ENABLED')}
              valueOff={$format('label.DISABLED')}
              showActions={false}
              editing={true}
              checked={outlookaddin.mergeParams}
              onChange={(id, label, value) => onOutlookAddinChange('mergeParams', value)}
            />

            {#if outlookaddin.mergeParams}
              <SwitchField
                id="outlookaddin-ignoreblankmergefields"
                label={$format('label.IGNORE_EMPTY_FIELDS')}
                tooltip={$format('label.TOOLTIP.IGNORE_EMPTY_FIELDS')}
                valueOn={$format('label.ENABLED')}
                valueOff={$format('label.DISABLED')}
                showActions={false}
                editing={true}
                checked={outlookaddin.ignoreblankmergefields}
                onChange={(id, label, value) => onOutlookAddinChange('ignoreblankmergefields', value)}
              />
            {/if}
          {/if}
        </Card>
      </div>
    {/if}
    <div class="section mt-5 mb-4">
      <h2>{$format('label.ADDIN_CUSTOM_FIELDS')}</h2>
      <CustomFields bind:fields={helpers} editing />
    </div>
  {/if}
</div>

<style lang="scss">
  .group-edit-form {
    .section + .section {
      margin-top: var(--margin-xxlarge);
    }

    .signature-mode-field {
      border-bottom: 1px solid var(--border-color);
      padding: var(--padding-large) 0;
    }

    :global(.select-element) {
      --item-height: 100%;
      --item-line-height: 1.2rem;
    }

    .tab-content {
      padding: var(--padding-large) 0;
    }

    .dot {
      background-color: var(--color-green-400);
      border-radius: 100%;
      content: '';
      display: inline-block;
      height: 10px;
      margin-left: var(--margin-base);
      width: 10px;
    }
  }
</style>
